import React, { useMemo } from 'react';
import { styled } from '@mui/material/styles';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm'; // Importar remark-gfm para soporte de tablas
import CodeBlock from './CodeBlock';

// Cached styled component to prevent recreation on each render
const StyledReactMarkdown = styled(ReactMarkdown)(({ theme }) => ({
  width: '100%',
  // Estilos para diferentes elementos Markdown
  '& p, & li': {
    margin: '0 0 16px 0',
    lineHeight: '1.75',
    '&:last-of-type': {
      marginBottom: 0,
    },
  },
  '& ul, & ol': {
    margin: '0 0 16px 1.5em',
    padding: 0,
  },
  '& li': {
    margin: '0 0 8px 0',
    lineHeight: '1.75',
    '& > p': {
      margin: '0 0 8px 0',
    },
  },
  '& blockquote': {
    borderLeft: `3px solid ${theme.palette.primary.main}`,
    paddingLeft: '1em',
    margin: '1em 0',
    fontStyle: 'italic',
  },
  '& img': {
    maxWidth: '100%',
    height: 'auto',
    borderRadius: '8px',
  },
  // Mejorar estilos de tabla con bordes redondeados
  '& table': {
    width: '100%',
    borderCollapse: 'separate', // Cambiado a separate para permitir border-radius
    borderSpacing: 0, // Eliminar espacio entre celdas
    marginBottom: '1em',
    fontSize: '0.95em',
    tableLayout: 'fixed', // Ayuda con tablas anchas
    overflow: 'hidden', // Para contener tablas grandes
    borderRadius: '8px', // Radio para la tabla completa
    border: `1px solid ${theme.palette.divider}`,
  },
  '& th, & td': {
    border: 0, // Quitar bordes individuales
    borderBottom: `1px solid ${theme.palette.divider}`,
    borderRight: `1px solid ${theme.palette.divider}`,
    padding: '8px 12px',
    textAlign: 'left',
    verticalAlign: 'top',
    wordBreak: 'break-word', // Para manejar contenido largo
    '&:last-child': {
      borderRight: 0, // Quitar borde derecho de la última columna
    },
  },
  '& tr:last-child td': {
    borderBottom: 0, // Quitar borde inferior de la última fila
  },
  '& th': {
    backgroundColor: theme.palette.mode === 'dark'
      ? 'rgba(255, 255, 255, 0.08)'
      : 'rgba(0, 0, 0, 0.04)',
    fontWeight: 'bold',
    // Añadir bordes redondeados solo a las esquinas superiores de la primera fila
    '&:first-of-type': {
      borderTopLeftRadius: '8px',
    },
    '&:last-child': {
      borderTopRightRadius: '8px',
    },
  },
  '& tr:last-child td:first-of-type': {
    borderBottomLeftRadius: '8px', // Esquina inferior izquierda
  },
  '& tr:last-child td:last-child': {
    borderBottomRightRadius: '8px', // Esquina inferior derecha
  },
  '& tr:nth-of-type(even)': {
    backgroundColor: theme.palette.mode === 'dark' 
      ? 'rgba(255, 255, 255, 0.03)' 
      : 'rgba(0, 0, 0, 0.02)',
  },
  '& hr': {
    border: 'none',
    borderTop: `1px solid ${theme.palette.divider}`,
    margin: '1em 0',
  },
  '& a': {
    color: theme.palette.primary.main,
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  '& pre': {
    margin: '16px 0',
    padding: 0,
    backgroundColor: 'transparent',
  },
  '& pre > code': {
    padding: 0,
    backgroundColor: 'transparent',
  },
  '& :first-of-type': {
    marginTop: 0,
  },
  '& :last-of-type': {
    marginBottom: 0,
  },
}));

// Componentes memoizados para React Markdown
const MemoizedCodeComponent = React.memo(({ node, inline, className, children, theme, ...props }) => {
  const match = /language-(\w+)/.exec(className || '');
  const language = match ? match[1] : '';

  if (!inline && language) {
    return (
      <CodeBlock 
        code={String(children).trim()}
        language={language}
        shouldAnimate={false}
        isLoading={false}
        onAnimationComplete={() => {}}
      />
    );
  }

  return (
    <code 
      className={className} 
      style={{
        padding: '2px 4px',
        borderRadius: '4px',
        fontSize: '0.9em',
        backgroundColor: theme?.palette.mode === 'dark' 
          ? 'rgba(255, 255, 255, 0.1)' 
          : 'rgba(0, 0, 0, 0.1)',
        fontFamily: 'var(--font-mono)',
      }}
      {...props}
    >
      {children}
    </code>
  );
});

MemoizedCodeComponent.displayName = 'MemoizedCodeComponent';

const MemoizedLinkComponent = React.memo(({ node, children, href, theme, ...props }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    style={{
      color: theme?.palette.primary.main,
      textDecoration: 'none',
    }}
    {...props}
  >
    {children}
  </a>
));

MemoizedLinkComponent.displayName = 'MemoizedLinkComponent';

const MemoizedImageComponent = React.memo(({ src, alt, ...props }) => (
  <img 
    src={src} 
    alt={alt || ''} 
    loading="lazy"
    {...props} 
  />
));

MemoizedImageComponent.displayName = 'MemoizedImageComponent';

const MemoizedPreComponent = React.memo(({ node, children, ...props }) => children);

MemoizedPreComponent.displayName = 'MemoizedPreComponent';

// Componente de tabla mejorado con sombra suave y bordes redondeados
const MemoizedTableComponent = React.memo(({ children, ...props }) => (
  <div style={{ 
    overflowX: 'auto', 
    width: '100%',
    borderRadius: '8px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)'
  }}>
    <table {...props}>{children}</table>
  </div>
));

MemoizedTableComponent.displayName = 'MemoizedTableComponent';

// Componente principal MarkdownRenderer
const MarkdownRenderer = ({ content, theme }) => {
  // Crear renderizadores personalizados solo una vez por cambio de tema
  const customRenderers = useMemo(() => ({
    code: (props) => <MemoizedCodeComponent {...props} theme={theme} />,
    a: (props) => <MemoizedLinkComponent {...props} theme={theme} />,
    img: (props) => <MemoizedImageComponent {...props} />,
    pre: (props) => <MemoizedPreComponent {...props} />,
    // Añadir componentes específicos para tablas
    table: (props) => <MemoizedTableComponent {...props} theme={theme} />,
  }), [theme]);

  // Los plugins son estáticos, así que podemos memorizarlos
  const remarkPlugins = useMemo(() => [remarkGfm], []); // Añadir remarkGfm para soporte de tablas
  const rehypePlugins = useMemo(() => [rehypeRaw], []);

  return (
    <StyledReactMarkdown
      remarkPlugins={remarkPlugins}
      rehypePlugins={rehypePlugins}
      components={customRenderers}
      className="markdown-content"
    >
      {content}
    </StyledReactMarkdown>
  );
};

// Usar React.memo con una función de igualdad personalizada para comparación profunda del contenido
export default React.memo(MarkdownRenderer, (prevProps, nextProps) => {
  // Comparación simple de contenido
  const contentEqual = prevProps.content === nextProps.content;
  
  // Comparación de tema - los temas son objetos complejos, así que compara por modo
  const themeEqual = 
    (!prevProps.theme && !nextProps.theme) || 
    (prevProps.theme?.palette?.mode === nextProps.theme?.palette?.mode);
  
  return contentEqual && themeEqual;
});